/**
 * Demo bookings — everything booked on Calendly, and the work done on each.
 *
 * The screen follows the demo cycle: a booking arrives, is matched to the
 * Client Master - matched, or marked registered by hand - the demo happens or
 * does not, an outcome is recorded, and somebody owns what happens next.
 *
 * Calendly owns the slot, the person, the reschedule, the cancellation and
 * the no-show; none of that is editable here and the row links out to
 * Calendly for it. What is ours is who this is, what came of the demo, the
 * feedback, and the follow-up.
 *
 * Two kinds of customer, worked differently:
 *
 *   B2C  one demo, then the sales team follows up - to answer questions, to
 *        move them onto a plan, or simply to get them registered
 *   B2B  a partner running a course of sessions; each session is closed with
 *        feedback rather than a sales task, and only escalates when somebody
 *        asks for it
 *
 * Every time on this screen is Dubai's - see domain/demos.js.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import ManualMeeting from '../components/modals/ManualMeeting.jsx';
import { useApp } from '../store/AppContext.jsx';
import api from '../lib/api.js';
import { formatDate } from '../lib/dates.js';
import { download } from '../lib/format.js';
import { describePhone } from '../lib/phone.js';
import Icon from '../lib/icons.jsx';
import { Card, CardBody, EmptyState, Avatar, Pill, Spinner } from '../components/Primitives.jsx';
import Sheet, { Group } from '../components/Sheet.jsx';
import { Attendees, RowMenu, AttendeePicker, PlanPicker, AssigneeBook } from '../components/DemoAssign.jsx';
import { FollowJourney } from '../components/FollowJourney.jsx';
import {
  DAY, TZ_LABEL, hhmm, dayText, dayLabel, stamp, workStamp, dubaiDayStart, dubaiToday,
  stateOf, STATE_META, needsAction, isPartner, isWorkable, followState, FOLLOW_META, CLOSED_META,
  REGISTERED, REG_META, regLabel, pendingClose, followUps, earlierDemos, laterDemos, ago
} from '../domain/demos.js';

/* The time windows on offer. `from`/`to` are offsets in days from the start
   of today in Dubai; null means open-ended on that side. */
const WHEN = [
  ['upcoming', 'Upcoming', 0, null],
  ['today', 'Today', 0, 1],
  ['week', 'Next 7 days', 0, 7],
  ['month', 'Next 30 days', 0, 30],
  ['past7', 'Past 7 days', -7, 0],
  ['past30', 'Past 30 days', -30, 0],
  ['thismonth', 'This month', 'month', null],
  ['all', 'Everything', null, null],
  ['custom', 'Custom range…', null, null]
];

/*
 * Which way a window reads by default.
 *
 * What is coming reads top-down, because the row that matters is the next
 * one. What has already happened reads newest first, for the same reason
 * from the other end. It is only a default: the control beside the window
 * flips it, since "which was the first demo this month" is a fair question
 * and scrolling to the bottom is not an answer.
 */
const ASC_BY_DEFAULT = ['upcoming', 'today', 'week', 'month'];
const autoAsc = w => ASC_BY_DEFAULT.includes(w);

/* What the booking itself is. Everything else a tile asks for - needs
   action, following up, not registered - is a cut the tiles make, not a
   state of the booking, so it is not offered twice. */
const STATUS = [
  ['', 'Any status'],
  ['upcoming', 'Upcoming'],
  ['done', 'Completed'],
  ['noshow', 'No-show'],
  ['rescheduled', 'Rescheduled'],
  ['canceled', 'Cancelled']
];

/*
 * The three views of the same book.
 *
 * The diary is the job of this page; the other two are a window onto what
 * sales is doing with the demos that came out of it, so that the team who
 * ran the demo can see where their hand-over got to without going to the
 * Sales module - and without being able to touch it. They are read-only on
 * purpose: the follow-up belongs to whoever is chasing it.
 */
const TABS = [
  ['bookings', 'Demo bookings', 'cal'],
  /*
   * Two states, one tab.
   *
   * A hand-over lands at 'handed' - with sales, nobody has picked it up -
   * and only becomes 'following' when somebody starts chasing. This tab
   * read 'following' alone, so every demo that had just been handed over
   * was in neither tab: thirty-four no-shows that had gone to sales, and
   * a screen that looked as though nothing had happened to them.
   */
  ['following', 'With sales', 'flag', ['handed', 'following']],
  ['closed', 'Closed', 'check', ['closed']]
];

/* Registration is not a booking status, so it gets its own control rather
   than being folded into one list with "no-show" and "cancelled". */
const REG = [
  ['', 'Anyone'],
  ['matched', 'Registered'],
  ['manual', 'Registered manually'],
  ['none', 'Not registered']
];

const unmatched = b => b.session && !b.session.clientId && !b.session.contactId;

/** Does this booking match what was typed? Shared by the three tabs. */
function hits(b, needle) {
  if (!needle) return true;
  const s = b.session || {};
  const hay = [
    b.invitee.name, b.invitee.email, b.invitee.phone, b.eventTypeName, b.host.name,
    b.client && b.client.name, b.client && b.client.code, b.contact && b.contact.name,
    b.contact && b.contact.code, s.ownerName, s.nextAction, s.demoNotes, s.business, s.interest,
    s.outcomeLabel, s.handedBy, s.handedNote, s.closedReason,
    ...(s.feedback || []).map(x => x.text), ...(b.invitee.answers || []).map(x => x.a)
  ].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(needle);
}

export default function DemoBookings() {
  const { toast, demos, loadDemos, patchDemo, refreshCrm, can, setSheet } = useApp();
  const location = useLocation();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [reg, setReg] = useState('');
  const [type, setType] = useState('');
  const [host, setHost] = useState('');
  const [when, setWhen] = useState('upcoming');
  /* Oldest first or newest first, starting from whatever the window
     usually wants. Changing the window starts it over. */
  const [asc, setAsc] = useState(autoAsc('upcoming'));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [clientsOnly, setClientsOnly] = useState(false);
  const [open, setOpen] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  /* Which row has its hand-over box open. */
  const [starting, setStarting] = useState(null);
  const [tab, setTab] = useState('bookings');
  /* The demo team list, and which row has a picker open. */
  const [book, setBook] = useState(false);
  const [people, setPeople] = useState(null);
  const [picking, setPicking] = useState(null);
  /* Adding a meeting Calendly never saw. */
  const [adding, setAdding] = useState(false);
  /* Which manual meeting is open for correction, if any. */
  const [editing, setEditing] = useState(null);

  const data = demos;
  const loading = data === null;
  const all = data?.bookings || [];
  const now = Date.now();
  const today = dubaiToday();
  const mayEdit = can('demo-bookings', 'edit');

  useEffect(() => { if (demos === null) loadDemos(); }, [demos, loadDemos]);

  /* Narrowing the list folds whatever was open, and puts away a hand-over
     box half written on a row that may no longer be there. The panel is
     taller than its row, so a filter that changes what is underneath leaves
     one row's detail sitting over another row's list. */
  useEffect(() => {
    setOpen(null);
    setStarting(null);
    setPicking(null);
  }, [q, status, reg, type, host, when, from, to, clientsOnly, tab]);

  /* The list arrives with the bookings; `people` holds it only while it is
     being edited, so a change shows before the next full reload. */
  const assignees = people || data?.assignees || [];

  /* Arriving from the bell or another screen with a booking to open: show it
     whatever the filters say, and open it. */
  useEffect(() => {
    const want = location.state && location.state.open;
    if (!want || !all.length) return;
    const hit = all.find(b => b.session && b.session.id === want);
    if (!hit) return;
    setWhen('all'); setStatus(''); setQ(''); setClientsOnly(false);
    setOpen(hit.id);
    window.history.replaceState({}, '');
    setTimeout(() => {
      const el = document.getElementById(`dbk-${hit.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, all.length]);

  /* The tiles count the whole book, not the filtered list, so they can be
     used to move between cuts without the numbers shifting underfoot. */
  const counts = useMemo(() => {
    const c = { upcoming: 0, today: 0, done: 0, noshow: 0, rescheduled: 0, canceled: 0 };
    all.forEach(b => {
      const s = stateOf(b, now);
      const start = new Date(b.start).getTime();
      if (s === 'upcoming' || s === 'live') {
        c.upcoming += 1;
        if (dubaiDayStart(start) === today) c.today += 1;
      }
      if (s === 'done') c.done += 1;
      if (s === 'noshow') c.noshow += 1;
      if (s === 'canceled') c.canceled += 1;
      if (b.rescheduled || b.rescheduledFrom) c.rescheduled += 1;
    });
    return c;
  }, [all, now, today]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let lo = null;
    let hi = null;
    const w = WHEN.find(x => x[0] === when) || WHEN[0];
    if (when === 'custom') {
      lo = from ? Date.parse(`${from}T00:00:00+04:00`) : null;
      hi = to ? Date.parse(`${to}T00:00:00+04:00`) + DAY : null;
    } else if (w[2] === 'month') {
      const d = new Date(today);
      lo = dubaiDayStart(new Date(d.getFullYear(), d.getMonth(), 1));
      hi = dubaiDayStart(new Date(d.getFullYear(), d.getMonth() + 1, 1));
    } else {
      lo = w[2] === null ? null : today + w[2] * DAY;
      hi = w[3] === null ? null : today + w[3] * DAY;
    }
    /* "Upcoming" means from now, not from midnight: a demo that ended an
       hour ago is not upcoming. */
    if (when === 'upcoming') lo = now;

    const list = all.filter(b => {
      /* The row being worked on stays put: marking somebody registered
         must not make
         it vanish from under the cut mid-way. */
      if (b.id === open) return true;
      const start = new Date(b.start).getTime();
      const end = new Date(b.end).getTime();
      const s = b.session || {};
      if (lo !== null && (when === 'upcoming' ? end : start) < lo) return false;
      if (hi !== null && start >= hi) return false;
      if (type === '@manual') { if (!(b.session && b.session.isManual)) return false; }
      else if (type === '@calendly') { if (b.session && b.session.isManual) return false; }
      else if (type && b.eventType !== type) return false;
      if (host && b.host.email !== host) return false;
      if (clientsOnly && !b.client) return false;
      if (reg && REGISTERED(b) !== reg) return false;
      if (status) {
        const st = stateOf(b, now);
        const ok = status === 'rescheduled' ? (b.rescheduled || b.rescheduledFrom)
          : status === 'upcoming' ? (st === 'upcoming' || st === 'live')
            : status === 'needs' ? !!needsAction(b, now)
              : status === 'unmatched' ? unmatched(b) && st !== 'canceled'
                : status === 'unregistered' ? !b.client && st !== 'canceled'
                  : status === 'tostart' ? isWorkable(b, now) && followState(b) === 'open'
                    : status === 'following' ? followState(b) === 'following'
                      : status === 'closed' ? s.followStatus === 'closed'
                        : status === 'converted' ? (s.followStatus === 'converted' || !!b.deal)
                          : st === status;
        if (!ok) return false;
      }
      if (!hits(b, needle)) return false;
      return true;
    });

    return list.sort((a, b) => (asc ? 1 : -1) * (new Date(a.start) - new Date(b.start)));
  }, [all, q, status, reg, type, host, when, from, to, clientsOnly, now, today, open, asc]);

  /* Rows under the day they fall on in Dubai, so a week reads like a diary. */
  const days = useMemo(() => {
    const out = [];
    let last = null;
    shown.forEach(b => {
      const key = dubaiDayStart(b.start);
      if (key !== last) { out.push({ key, at: b.start, rows: [] }); last = key; }
      out[out.length - 1].rows.push(b);
    });
    return out;
  }, [shown]);

  /**
   * Say by hand that this booking belongs to a client of ours.
   *
   * One press and done: the search that used to sit here asked which
   * account, which is the question nobody could answer - the whole reason
   * the row says "not registered" is that the address does not match any.
   */
  const markRegistered = async (b, on) => {
    try {
      await api.calendly.update(b.session.id, { manualRegistered: on });
      toast(on ? 'Marked as a registered client' : 'Manual mark removed');
      await loadDemos();
    } catch (e) {
      toast(e.message);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await loadDemos({ refresh: 1 });
      toast('Bookings refreshed from Calendly');
    } catch (e) {
      toast(e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const exportCsv = () => {
    const esc = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const head = ['Start (Dubai)', 'End (Dubai)', 'Status', 'Meeting', 'Name', 'Email', 'Phone', 'Business',
      'Customer type', 'Client ID', 'Account', 'Registered', 'Demo no', 'Attendance', 'Outcome',
      'Next action', 'Due', 'Owner', 'Follow-up', 'Feedback', 'Host', 'Booked at', 'Notes'];
    const rows = shown.map(b => {
      const s = b.session || {};
      const who = b.client || b.contact;
      return [
        stamp(b.start), stamp(b.end), STATE_META[stateOf(b, now)][0], b.eventTypeName,
        b.invitee.name, b.invitee.email, b.invitee.phone, s.business || '',
        s.customerType || '', who ? who.code : '', who ? who.name : '',
        b.client ? 'Matched' : s.manualRegistered ? 'By hand' : 'No',
        s.demoCount > 1 ? `${s.demoIndex} of ${s.demoCount}` : '1',
        s.attendance === 'no_show' ? 'No-show' : s.attendance === 'attended' ? 'Attended' : '',
        s.outcomeLabel || '', s.nextAction || '', s.nextDue || '', s.ownerName || '', s.followStatus || '',
        (s.feedback || []).map(f => `${f.by}: ${f.text}`).join(' | '),
        b.host.name, stamp(b.bookedAt),
        [s.demoNotes, s.interest].filter(Boolean).join(' | ')
      ];
    });
    const csv = [head, ...rows].map(r => r.map(esc).join(',')).join('\r\n');
    download(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }),
      `demo-bookings-${formatDate(new Date(), false, 'iso')}.csv`);
    toast(`Exported ${rows.length} booking${rows.length === 1 ? '' : 's'}`);
  };

  const pick = (nextWhen, nextStatus) => { setWhen(nextWhen); setStatus(nextStatus); };
  const tileOn = (w, s) => when === w && status === s;

  /* ------------------------------------------------------------ states */

  if (data && data.connected === false) {
    return (
      <Card>
        <CardBody>
          <EmptyState icon="cal" tone="g-cyan" title="Calendly is not connected">
            <p>
              Set <code>CALENDLY_API_TOKEN</code> in <code>backend/.env</code> to a personal access
              token from Calendly &rarr; Integrations &rarr; API &amp; webhooks, restart the API,
              and every booking appears here.
            </p>
          </EmptyState>
        </CardBody>
      </Card>
    );
  }

  if (data && data.error && !all.length) {
    return (
      <Card>
        <CardBody>
          <EmptyState icon="alert" tone="g-red" title="Could not reach Calendly">{data.error}</EmptyState>
        </CardBody>
      </Card>
    );
  }

  /* The diary's own counts. What is waiting on somebody, what is being
     chased and who is still not registered are the follow-up list's
     questions, and it asks them there. */
  const tiles = [
    { k: 'upcoming', n: counts.upcoming, l: 'Upcoming', i: 'cal', c: 'g-blue', w: 'upcoming', s: '' },
    { k: 'today', n: counts.today, l: 'Today', i: 'zap', c: 'g-green', w: 'today', s: 'upcoming' },
    { k: 'done', n: counts.done, l: 'Completed', i: 'check', c: 'g-purple', w: 'all', s: 'done' },
    { k: 'noshow', n: counts.noshow, l: 'No-shows', i: 'eyeoff', c: 'g-amber', w: 'all', s: 'noshow' },
    { k: 'rescheduled', n: counts.rescheduled, l: 'Rescheduled', i: 'refresh', c: 'g-orange', w: 'all', s: 'rescheduled' },
    { k: 'canceled', n: counts.canceled, l: 'Cancelled', i: 'x', c: 'g-slate', w: 'all', s: 'canceled' }
  ];

  const counted = states => (data?.bookings || [])
    .filter(b => b.session && states.includes(followState(b))).length;

  return (
    <div className="dbk">
      <nav className="dbk-tabs" role="tablist">
        {TABS.map(([k, label, icon, states]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={`dbk-tab${tab === k ? ' on' : ''}`}
            onClick={() => { setTab(k); setOpen(null); setQ(''); }}
          >
            <Icon name={icon} solid />
            {label}
            {states && !loading ? <span className="dbk-tabn">{counted(states)}</span> : null}
          </button>
        ))}

        {/* Who runs the demos. It belongs beside the tabs rather than in the
            filter bar: it is about the team, not about which rows show. */}
        <button type="button" className="dbk-team" onClick={() => setBook(true)} title="Who can be recorded as taking a demo">
          <Icon name="users" />
          Assignees
          {assignees.filter(a => a.active).length
            ? <span className="dbk-tabn">{assignees.filter(a => a.active).length}</span>
            : null}
        </button>
      </nav>

      {book ? (
        <AssigneeBook
          list={assignees}
          mayEdit={mayEdit}
          toast={toast}
          onChange={next => { setPeople(next); loadDemos(); }}
          onClose={() => setBook(false)}
        />
      ) : null}

      {tab !== 'bookings' ? (
        <FollowTab
          /* The states the tab stands for, not the tab's own name. */
          kind={(TABS.find(t => t[0] === tab) || [])[3] || [tab]}
          data={data}
          now={now}
          q={q}
          setQ={setQ}
          loading={loading}
          refresh={refresh}
          refreshing={refreshing}
          reload={loadDemos}
          refreshCrm={refreshCrm}
          setSheet={setSheet}
          toast={toast}
        />
      ) : (
      <>
      <div className="stats dbk-stats">
        {tiles.map(t => (
          <div
            key={t.k}
            className={`stat${tileOn(t.w, t.s) ? ' on' : ''}${t.hot && t.n ? ' hot' : ''}`}
            onClick={() => pick(tileOn(t.w, t.s) ? 'upcoming' : t.w, tileOn(t.w, t.s) ? '' : t.s)}
          >
            <div className={`ic ${t.c}`}><Icon name={t.i} solid /></div>
            <div>
              <div className="n">{loading ? '…' : t.n}</div>
              <div className="l">{t.l}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="utfilter dbk-filter">
        <div className="f grow">
          <label htmlFor="dbk-q">Search</label>
          <input
            id="dbk-q"
            value={q}
            placeholder="Name, email, phone, business, client ID, notes…"
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <div className="f">
          <label htmlFor="dbk-when">When</label>
          <select
            id="dbk-when"
            value={when}
            onChange={e => { setWhen(e.target.value); setAsc(autoAsc(e.target.value)); }}
          >
            {WHEN.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        {/* Next to the window it reorders, and saying which way the dates
            are running now rather than which way the button would turn
            them. */}
        <div className="f">
          <label htmlFor="dbk-order">Order</label>
          <button
            id="dbk-order"
            type="button"
            className={`dbk-order${asc ? ' asc' : ''}`}
            onClick={() => setAsc(v => !v)}
            title={asc
              ? 'By date, ascending — click for descending'
              : 'By date, descending — click for ascending'}
          >
            <Icon name="chev" />
            {asc ? 'Ascending' : 'Descending'}
          </button>
        </div>
        {when === 'custom' ? (
          <>
            <div className="f">
              <label htmlFor="dbk-from">From</label>
              <input id="dbk-from" type="date" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="f">
              <label htmlFor="dbk-to">To</label>
              <input id="dbk-to" type="date" value={to} onChange={e => setTo(e.target.value)} />
            </div>
          </>
        ) : null}
        <div className="f">
          <label htmlFor="dbk-status">Status</label>
          <select
            id="dbk-status"
            value={STATUS.some(([k]) => k === status) ? status : ''}
            onChange={e => setStatus(e.target.value)}
          >
            {STATUS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        <div className="f">
          <label htmlFor="dbk-reg">Registered</label>
          <select id="dbk-reg" value={reg} onChange={e => setReg(e.target.value)}>
            {REG.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
        {(data?.eventTypes || []).length > 1 ? (
          <div className="f">
            <label htmlFor="dbk-type">Meeting</label>
            <select id="dbk-type" value={type} onChange={e => setType(e.target.value)}>
              <option value="">Any meeting</option>
              {data.eventTypes.map(t => <option key={t.id} value={t.id}>{t.name} · {t.duration} min</option>)}
              {/* Not meeting types - how the booking got here. A meeting
                  added by hand carries no Calendly event type at all, so
                  there is nowhere else to ask for it, and the two belong
                  on the control that already cuts the list by what kind
                  of booking this is. Prefixed so neither can ever collide
                  with a Calendly event type id. */}
              <option value="@manual">Manually added</option>
              <option value="@calendly">Booked on Calendly</option>
            </select>
          </div>
        ) : null}
        {(data?.hosts || []).length > 1 ? (
          <div className="f">
            <label htmlFor="dbk-host">Host</label>
            <select id="dbk-host" value={host} onChange={e => setHost(e.target.value)}>
              <option value="">Anyone</option>
              {data.hosts.map(h => <option key={h.email} value={h.email}>{h.name || h.email}</option>)}
            </select>
          </div>
        ) : null}
        <button
          type="button"
          className={`btn${clientsOnly ? ' on' : ''}`}
          onClick={() => setClientsOnly(v => !v)}
          title="Only bookings from people who already have an account"
        >
          <Icon name="users" />Clients
        </button>
        {mayEdit ? (
          <button
            type="button"
            className="btn"
            onClick={() => setAdding(true)}
            title="A meeting that was not booked through Calendly"
          >
            <Icon name="plus" />Add a meeting
          </button>
        ) : null}
        <button type="button" className="btn" onClick={exportCsv} disabled={!shown.length} title="Export what is listed">
          <Icon name="down" />Export
        </button>
        <button type="button" className="btn" onClick={refresh} disabled={refreshing} title="Read Calendly again now">
          {refreshing ? <Spinner /> : <Icon name="refresh" />}Refresh
        </button>
      </div>

      {loading ? (
        <p className="muted utload"><Spinner /> Reading bookings from Calendly…</p>
      ) : !shown.length ? (
        <Card>
          <CardBody>
            <EmptyState icon="cal" tone="g-cyan" title={all.length ? 'Nothing here' : 'No bookings yet'}>
              {all.length
                ? 'Nothing matches that filter — try a wider window or clear the search.'
                : 'When somebody books a demo on your Calendly page it appears here.'}
            </EmptyState>
          </CardBody>
        </Card>
      ) : (
        <div className="dbk-days">
          {days.map(day => (
            <section className="dbk-day" key={day.key}>
              <header className="dbk-dayhead">
                <b>{dayLabel(day.at, today)}</b>
                <span>{dayText(day.at)} · {day.rows.length} booking{day.rows.length === 1 ? '' : 's'}</span>
              </header>
              <div className="dbk-list">
                {day.rows.map(b => (
                  <BookingRow
                    key={b.id}
                    b={b}
                    data={data}
                    now={now}
                    open={open === b.id}
                    mayEdit={mayEdit}
                    /* A row waiting on a Close does not fold away — the
                       button only exists inside the panel. */
                    onToggle={() => {
                      if (open === b.id && mayEdit && pendingClose(b)) {
                        toast('Press Close to finish this follow-up first');
                        return;
                      }
                      setOpen(open === b.id ? null : b.id);
                    }}
                    onClose={() => setOpen(null)}
                    onEdit={setEditing}
                    reload={loadDemos}
                    refreshCrm={refreshCrm}
                    setSheet={setSheet}
                    toast={toast}
                    starting={starting === b.id}
                    onStart={() => { setStarting(b.id); setOpen(null); }}
                    onCancelStart={() => setStarting(null)}
                    onStarted={session => {
                      setStarting(null);
                      /* Now, then properly. */
                      if (session) patchDemo(session.id, session);
                      loadDemos();
                    }}
                    assignees={assignees}
                    onAssignees={setPeople}
                    picking={picking && picking.id === b.id ? picking.kind : ''}
                    onPick={kind => {
                      if (kind === 'unmark') { markRegistered(b, false); return; }
                      setPicking({ id: b.id, kind });
                      setOpen(null);
                      setStarting(null);
                    }}
                    onPicked={() => { setPicking(null); loadDemos(); }}
                    onCancelPick={() => setPicking(null)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <p className="cvnote dbk-note">
        {data?.fetchedAt ? `Read from Calendly ${ago(data.fetchedAt, now)}` : ''}
        {data?.stale ? ' — Calendly did not answer just now, so this is the last good copy.' : '.'}
        {' '}Showing the last {data?.daysBack || 180} days and everything ahead.
        {' '}<b>All times are {TZ_LABEL}.</b>
      </p>
      </>
      )}

      {adding || editing ? (
        <ManualMeeting
          booking={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={async when => {
            /* The list opens on "Upcoming", and the meetings worth adding
               by hand are mostly ones that already happened — so a row
               added here would be saved, correct, and nowhere on screen.
               Widen the window to the one that contains it. */
            if (when && new Date(when).getTime() < Date.now()) setWhen('all');
            await loadDemos();
          }}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------- the read-only tabs */

/**
 * Following up / Closed: where the hand-overs from this page got to.
 *
 * Nothing here can be changed - the rows open to the same record the
 * booking carries, without the panels that edit it. Sales does the work on
 * Sales -> Follow-ups; this is the demo team watching it happen.
 */
function FollowTab({ kind, data, now, q, setQ, loading, refresh, refreshing, reload, refreshCrm, setSheet, toast }) {
  const [open, setOpen] = useState(null);
  /* Which half of "with sales". Empty means everything, which is what
     the tab showed before and is still the way in. */
  const [cut, setCut] = useState('');

  /* Everything this tab could show, before the two controls narrow it -
     the counts on them have to be of the pile, not of what is left after
     they have been used. */
  const base = useMemo(
    () => followUps(data).filter(b => kind.includes(followState(b))),
    [data, kind]
  );

  /*
   * "With sales" is two states in one tab: handed over and nobody has
   * started, or somebody is chasing. They are the same row to the demo
   * team and two entirely different questions to whoever runs sales -
   * the first is a queue, the second is work in progress - so the tab
   * splits without becoming two tabs.
   *
   * Only there. The Closed tab is one state and a strip saying so would
   * be a control with one answer.
   */
  const split = kind.length > 1;
  const nHanded = useMemo(() => base.filter(b => followState(b) === 'handed').length, [base]);

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return base
      .filter(b => !cut || followState(b) === cut)
      /* The open row stays whatever the filter says - folding away the
         panel somebody is reading because they typed in the search box
         is the search box closing their work. */
      .filter(b => b.id === open || hits(b, needle));
  }, [base, cut, q, open]);

  return (
    <>
      {split ? (
        <div className="dbk-cuts">
          <div className="handtabs" role="tablist" aria-label="How far it has got">
            {[['', 'Everything', base.length],
              ['handed', 'Not started', nHanded],
              ['following', 'Following up', base.length - nHanded]].map(([k, label, n]) => (
                <button
                  key={k || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={cut === k}
                  className={cut === k ? 'on' : undefined}
                  onClick={() => setCut(k)}
                >
                  {label}<b>{n}</b>
                </button>
              ))}
          </div>
        </div>
      ) : null}

      <div className="utfilter dbk-filter">
        <div className="f grow">
          <label htmlFor="dbk-fq">Search</label>
          <input
            id="dbk-fq"
            value={q}
            placeholder="Name, business, client ID, feedback…"
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <button type="button" className="btn" onClick={refresh} disabled={refreshing} title="Read Calendly again now">
          {refreshing ? <Spinner /> : <Icon name="refresh" />}Refresh
        </button>
      </div>

      {loading ? (
        <p className="muted utload"><Spinner /> Reading bookings from Calendly…</p>
      ) : !list.length ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={kind.includes('closed') ? 'check' : 'flag'}
              tone={kind.includes('closed') ? 'g-slate' : 'g-blue'}
              title={cut ? 'Nothing matches that'
                : kind.includes('closed') ? 'Nothing closed yet' : 'Nothing is with sales'}
            >
              {cut
                ? 'There are rows on this tab, just none in this half of it.'
                : kind.includes('closed')
                  ? 'A follow-up appears here once sales closes it with a reason.'
                  : 'A demo appears here the moment it is handed to sales — a no-show does that by itself.'}
            </EmptyState>
          </CardBody>
        </Card>
      ) : (
        <div className="dbk-list">
          {list.map(b => (
            <BookingRow
              key={b.id}
              b={b}
              data={data}
              now={now}
              open={open === b.id}
              mayEdit={false}
              onToggle={() => setOpen(open === b.id ? null : b.id)}
              reload={reload}
              refreshCrm={refreshCrm}
              setSheet={setSheet}
              toast={toast}
              withDate
            />
          ))}
        </div>
      )}

      <p className="cvnote dbk-note">
        {kind === 'closed'
          ? 'Demos sales has finished with, and the reason each was closed.'
          : 'Demos sales has picked up and is chasing now.'}
        {' '}Read-only here — the work is done on <b>Sales → Follow-ups</b>.
        {' '}<b>All times are {TZ_LABEL}.</b>
      </p>
    </>
  );
}

/* ------------------------------------------------------------------ row */

function BookingRow({
  /* `onClose` is not `onToggle` run backwards: toggling refuses while a
     follow-up is waiting to be closed, and marking a booking B2B or
     removing it has to fold the panel either way. */
  b, data, now, open, mayEdit, onToggle, onClose, onEdit, reload, refreshCrm, setSheet, toast,
  starting, onStart, onCancelStart, onStarted, withDate,
  assignees = [], onAssignees, picking = '', onPick, onPicked, onCancelPick
}) {
  const state = stateOf(b, now);
  const [label, tone, icon] = STATE_META[state];
  /*
   * Which way this badge can be corrected, if either — see the note on
   * the badge itself.
   *
   * Completed is the clock's guess and can be overruled. A no-show is
   * somebody's answer, so it can only be taken back by whoever gave it:
   * a real Calendly no-show stays as Calendly has it.
   */
  const flipTo = !mayEdit || !b.session ? ''
    : stateOf(b, now) === 'done' ? 'no_show'
      : stateOf(b, now) === 'noshow' && b.session.attendanceManual ? 'attended' : '';
  const [flip, setFlip] = useState(false);
  const ph = describePhone(b.invitee.phone);
  const soon = state === 'upcoming' && new Date(b.start).getTime() - now < 60 * 60000;
  const s = b.session;
  const todo = needsAction(b, now);
  const fs = s ? followState(b) : 'open';
  /* How it ended, where somebody said — see the badge below. */
  const endedAs = s && fs === 'closed' && s.closedAs && s.closedAs !== 'other'
    ? (CLOSED_META[s.closedAs] || null) : null;
  /* The Follow up button says the same thing as the nudge beside it, so
     only one of them appears: the prompt is for the rows that cannot be
     acted on from here - the read-only tabs, and anybody without the
     permission to work them. */
  const canStart = mayEdit && s && isWorkable(b, now) && fs === 'open';
  const overdue = s && fs === 'following' && s.nextDue
    && Date.parse(`${s.nextDue}T23:59:59+04:00`) < now;
  const who = b.client || b.contact;

  return (
    <article id={`dbk-${b.id}`} className={`dbk-row ${state}${open ? ' open' : ''}${todo ? ' todo' : ''}`}>
      {/* A div, not a button: the row carries links of its own, and a link
          inside a button is not something a browser can be relied on to
          click. */}
      <div
        className="dbk-main"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <div className="dbk-time">
          <b>{withDate ? dayShort(b.start) : hhmm(b.start)}</b>
          <small>{withDate ? hhmm(b.start) : `${hhmm(b.end)} · ${b.duration} min`}</small>
        </div>

        <div className="dbk-who">
          <Avatar name={b.invitee.name || b.invitee.email || '?'} />
          <div>
            <b>
              {b.invitee.name || b.invitee.email || 'Unknown invitee'}
              {s && s.customerType ? (
                <Pill
                  tone={s.customerType === 'B2B' ? 'gr-indigo' : 'gr-cyan'}
                  className="dbk-client"
                  title={`Booked as "${b.eventTypeName || b.name}"`}
                >
                  {s.customerType === 'B2B' ? 'B2B partner' : 'B2C customer'}
                </Pill>
              ) : null}
              <WhoPill b={b} />
              {s && s.demoCount > 1 ? (
                <Pill tone="gr-violet" className="dbk-client">Demo {s.demoIndex} of {s.demoCount}</Pill>
              ) : null}
              {b.rescheduled || b.rescheduledFrom ? (
                <Pill tone="gr-amber" icon="refresh" className="dbk-client">Rescheduled</Pill>
              ) : null}
              {/* Said on the row, because it changes how it is read:
                  this one was never on Calendly. */}
              {s && s.isManual ? (
                <Pill tone="gr-teal" icon="edit" className="dbk-client" title="Arranged directly and entered here — this meeting was never booked on Calendly">
                  Manually added
                </Pill>
              ) : null}
              {/*
                * Closed, and how.
                *
                * "Closed" on its own was the one thing every finished
                * follow-up said, so the tab read as a list of identical
                * grey badges and the interesting part — did they buy, or
                * did they say no — was a sentence away in the middle
                * column. The category is stored on the row already; it
                * just was not being shown. Falls back to plain Closed
                * where nobody said which, which is what the older rows
                * are.
                */}
              {endedAs ? (
                /* "Closed with Demo rescheduled", not "Demo rescheduled":
                   the category on its own reads as something that has just
                   happened, when the fact the badge is carrying is that the
                   chase is over. */
                <Pill tone={endedAs[1]} icon={endedAs[2]} className="dbk-client" title={s.closedReason || ''}>
                  Closed with {endedAs[0].toLowerCase()}
                </Pill>
              ) : FOLLOW_META[fs] ? (
                <Pill tone={FOLLOW_META[fs][1]} icon={FOLLOW_META[fs][2]} className="dbk-client">
                  {FOLLOW_META[fs][0]}
                </Pill>
              ) : null}
            </b>
            <small>
              {who && who.code ? <span className="dbk-code">{who.code}</span> : null}
              {s && s.business ? `${s.business} · ` : ''}
              {b.invitee.email || '—'}
              {ph.digits ? <> · {ph.flag ? <span className="cpflag">{ph.flag}</span> : null} {ph.text}</> : null}
            </small>
            <Attendees list={s ? s.attendees : null} />
          </div>
        </div>

        <div className="dbk-meet">
          {s && s.handedBy && fs !== 'open' ? (
            /* Once it has been handed over, the useful thing to read is who
               sent it and what they said - not the meeting name, and not a
               next action that sales wrote for itself. */
            <>
              <b>
                {fs === 'closed'
                  ? `Closed${endedAs ? ` with ${endedAs[0]}` : ''}${s.closedReason ? ` — ${s.closedReason}` : ''}`
                  : `Handed over by ${s.handedBy}`}
              </b>
              <small title={s.handedNote || ''}>
                {s.handedNote ? `“${s.handedNote}”` : s.nextAction || 'No note was written'}
              </small>
            </>
          ) : s && (s.outcomeLabel || s.nextAction) ? (
            <>
              <b>{s.outcomeLabel || ''}</b>
              <small className={overdue ? 'bad' : ''}>
                {s.nextAction ? `→ ${s.nextAction}` : ''}
                {s.nextDue ? ` · ${overdue ? 'overdue ' : 'by '}${shortDate(s.nextDue)}` : ''}
                {s.ownerName ? ` · ${s.ownerName}` : ''}
              </small>
            </>
          ) : (
            <>
              <b>{b.eventTypeName || b.name}</b>
              <small>
                {b.where.label || '—'}
                {b.host.name ? ` · with ${b.host.name}` : ''}
              </small>
            </>
          )}

        </div>

        <div className="dbk-state">
          {/*
            * Completed is the one badge worth arguing with.
            *
            * It is set by the clock — the meeting's end time passed, so
            * the row says it happened — and the clock cannot know that
            * nobody turned up. Whoever was in the call knows, and they
            * are looking straight at the badge that is wrong. So the
            * badge is the control: press it, confirm, and it becomes a
            * no-show and goes to sales, exactly as pressing No-show on
            * the day would have done.
            *
            * Only Completed. Upcoming and Happening now are statements
            * about the clock and nothing else, and the other two are
            * already answers somebody gave.
            */}
          {flipTo ? (
            <button
              type="button"
              className="dbk-flip"
              title={flipTo === 'no_show'
                ? 'They did not turn up after all — switch it to a no-show'
                : 'They did turn up after all — switch it back to completed'}
              onClick={e => { e.stopPropagation(); setFlip(true); }}
            >
              <Pill tone={tone} icon={icon}>{label}</Pill>
            </button>
          ) : (
            <Pill tone={tone} icon={icon}>{state === 'upcoming' && soon ? 'Starting soon' : label}</Pill>
          )}
          {todo && !canStart ? <small className="bad">{todo}</small>
            : s && fs === 'closed'
              ? <small>Closed{endedAs ? ` with ${endedAs[0]}` : ''}{s.closedReason ? ` · ${s.closedReason}` : ''}</small>
              : s && (s.feedback || []).length
                ? <small>{s.feedback.length} feedback</small>
                : state === 'canceled' && b.cancellation ? <small>by {b.cancellation.by}</small> : null}
        </div>

        {/* The hand-over, on the row itself: a demo that happened or was a
            no-show is where sales takes it on, and making somebody open the
            panel to start that put a click between them and the job. */}
        <div className="dbk-go" onClick={e => e.stopPropagation()}>
          {/* A meeting added by hand, now over and not yet passed on: the
              two outcomes sit in place of the Follow up button, because
              which of them it was is the thing that has to be said first
              and Calendly is not here to say it. Completed goes straight
              on to the hand-over.

              They go the moment it is with sales, exactly as the Follow
              up button does - the question has been answered and the row
              has moved on. Changing the answer afterwards is a rarer
              thing, and it is in the panel. */}
          {mayEdit && s && s.isManual && (state === 'done' || state === 'noshow') && fs === 'open' ? (
            <Outcome b={b} reload={reload} toast={toast} onCompleted={() => onStart(b)} />
          ) : canStart ? (
            <button type="button" className="btn sm primary" onClick={() => onStart(b)}>
              <Icon name="flag" />Follow up
            </button>
          ) : null}
          {mayEdit && s && onPick ? (
            <RowMenu
              items={[
                /* Only a demo that actually happened: there is nobody to
                   record for a no-show, a cancellation or a slot that has
                   not come round yet. */
                state === 'done' ? {
                  label: s.attendees && s.attendees.length ? 'Change who took it' : 'Demo completed by…',
                  hint: 'Who was in the room',
                  icon: 'users',
                  run: () => onPick('who')
                } : null,
                /* Nothing to say about somebody the match already found;
                   this is for the client who booked with an address we do
                   not have, and for undoing that when it was wrong. */
                b.client ? null : {
                  label: s.manualRegistered ? 'Not registered after all' : 'Mark as registered',
                  hint: s.manualRegistered
                    ? 'Take the manual mark off'
                    : 'They are a client, under a different email',
                  icon: s.manualRegistered ? 'undo' : 'check',
                  run: () => onPick(s.manualRegistered ? 'unmark' : 'mark')
                }
              ]}
            />
          ) : null}
        </div>
      </div>

      {picking === 'mark' ? (
        <PlanPicker s={s} toast={toast} onCancel={onCancelPick} onDone={onPicked} />
      ) : null}
      {picking === 'who' ? (
        <AttendeePicker
          s={s}
          list={assignees}
          toast={toast}
          reloadAssignees={onAssignees}
          onCancel={onCancelPick}
          onDone={onPicked}
        />
      ) : null}


      {starting ? (
        <StartFollowUp
          b={b}
          onCancel={onCancelStart}
          onDone={onStarted}
          toast={toast}
        />
      ) : null}

      {flip ? (
        <SwitchAttendance
          b={b}
          to={flipTo}
          reload={reload}
          toast={toast}
          onClose={() => setFlip(false)}
        />
      ) : null}

      {open ? (
        <div className="dbk-detail">
          {s ? (
            <DemoWork
              b={b}
              data={data}
              state={state}
              mayEdit={mayEdit}
              reload={reload}
              refreshCrm={refreshCrm}
              setSheet={setSheet}
              toast={toast}
              compact
            />
          ) : null}

          {/* What sales has done about it, read-only: the booking screen
              is where the demo team watches the hand-over travel, and the
              work itself is done on Sales -> Follow-ups. */}
          {s && followState(b) !== 'open' ? (
            <FollowJourney b={b} mayEdit={false} readOnly reload={reload} toast={toast} />
          ) : null}

          <BookingDetail b={b} data={data} now={now} />

          {/* The panel folds after either action: the row is about to be
              redrawn as a partner or to disappear, and a panel left open
              over it describes a booking that is no longer there. */}
          {mayEdit ? <RowActions b={b} reload={reload} toast={toast} onDone={onClose} onEdit={onEdit} /> : null}
        </div>
      ) : null}
    </article>
  );
}

/**
 * Everything Calendly and the demo team recorded about one booking: the
 * facts of the meeting, every question the form asked and the answer given,
 * the earlier demos this person had, and the trail of what was done.
 *
 * Shared by the booking row and the follow-up row so that what sales reads
 * while chasing is exactly what the demo team wrote - the queries and the
 * requirements included, which is the whole reason a follow-up call goes
 * anywhere.
 */
export function BookingDetail({ b, data, now = Date.now() }) {
  const s = b.session;

  return (
    <>
          <dl className="dbk-facts">
            <div><dt>Booked</dt><dd>{stamp(b.bookedAt)} · {ago(b.bookedAt, now)}</dd></div>
            <div><dt>Meeting</dt><dd>{b.eventTypeName || b.name} · {b.duration} min{b.host.name ? ` · with ${b.host.name}` : ''}</dd></div>
            <div><dt>Their timezone</dt><dd>{b.invitee.timezone || '—'}</dd></div>
            <div>
              <dt>Where</dt>
              <dd>
                {b.where.label || '—'}{b.where.detail ? ` · ${b.where.detail}` : ''}
                {b.where.joinUrl ? <> · <a href={b.where.joinUrl} target="_blank" rel="noreferrer">open link</a></> : null}
              </dd>
            </div>
            {s && s.business ? <div><dt>Business</dt><dd>{s.business}</dd></div> : null}
            {b.guests.length ? <div><dt>Guests</dt><dd>{b.guests.join(', ')}</dd></div> : null}
            {b.source.source || b.source.campaign ? (
              <div><dt>Came from</dt><dd>{[b.source.source, b.source.medium, b.source.campaign].filter(Boolean).join(' / ')}</dd></div>
            ) : null}
            {b.cancellation ? (
              <div className="wide">
                <dt>Cancelled</dt>
                <dd>
                  by the {b.cancellation.by}{b.cancellation.at ? ` on ${stamp(b.cancellation.at)}` : ''}
                  {b.cancellation.reason ? <> — <i>{b.cancellation.reason}</i></> : ''}
                </dd>
              </div>
            ) : null}
            {(b.invitee.answers || []).map((x, i) => (
              <div className="wide" key={i}><dt>{x.q}</dt><dd className="ans">{x.a}</dd></div>
            ))}
          </dl>

          {/* Rescheduling and cancelling are done on Calendly itself, where
              they reach the invitee; offering them here only risked somebody
              moving a real booking while reading the record. */}
          <div className="dbk-links">
            <span className="dim">Booking {b.eventId.slice(0, 8)} · times in {TZ_LABEL}</span>
          </div>

          <EarlierDemos b={b} data={data} />

          {/* No history block. Every line it carried is said better where it
              belongs - the hand-over and the rounds are the journey, the
              match is the badge on the row - and repeating them as a log
              underneath made the panel end in a wall nobody read. */}
    </>
  );
}

/**
 * The two things that can be done to a booking itself.
 *
 * Correcting it to B2B: the meeting name decides partner or customer,
 * which is right for nearly every booking - but a partner who books
 * through the customer link is still a partner. Offered only on the
 * ones that are B2C now, because turning a B2B Meeting into a customer
 * would be contradicting the meeting that was actually booked.
 *
 * Removing it: a manual meeting is deleted outright. A Calendly one is
 * marked removed instead - the row is what stops the next sweep writing
 * it again - and disappears from the list, the counts and the exports
 * just the same.
 */
/**
 * Completed, or a no-show.
 *
 * A Calendly booking is told which by its host, over on Calendly: they
 * press "no-show" or they do not, and a slot that has passed without
 * one was attended. A meeting Calendly never saw has nowhere to press,
 * so the two live on the row instead, from the moment its half hour is
 * up. The clock's answer - completed - stands until somebody says
 * otherwise, and the one in force is the one lit up.
 *
 * Completed opens the hand-over there and then, because that is what
 * happens next to every demo that ran. No-show needs nothing more: one
 * of those takes itself to sales.
 */
function Outcome({ b, reload, toast, onCompleted }) {
  const [busy, setBusy] = useState(false);
  const noShow = b.session.attendance === 'no_show';

  const say = async v => {
    /* Already completed and pressed again: nothing to save, but the
       hand-over is still what they are reaching for. */
    if (v === 'attended' && !noShow) { onCompleted?.(); return; }
    setBusy(true);
    try {
      await api.calendly.update(b.session.id, { attendance: v });
      toast(v === 'attended' ? 'Marked as completed' : 'No-show — sales has it to chase');
      await reload();
      if (v === 'attended') onCompleted?.();
    } catch (e) { toast(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="dbk-answer">
      <button
        type="button"
        className={`btn sm${noShow ? '' : ' primary'}`}
        disabled={busy}
        onClick={() => say('attended')}
      >
        <Icon name="check" />Completed
      </button>
      <button
        type="button"
        className={`btn sm${noShow ? ' primary' : ''}`}
        disabled={busy}
        onClick={() => say('no_show')}
      >
        <Icon name="eyeoff" />No-show
      </button>
    </div>
  );
}

/**
 * "It says completed — it was not." And back again.
 *
 * One question and two answers, because the thing on the other side of
 * Yes is not small: the badge changes, and on a no-show the demo goes
 * to sales as a follow-up and somebody starts chasing. That is the same
 * hand-over the No-show button does on the day, by the same route, so
 * there is one way a no-show reaches sales and not two that could
 * drift apart.
 */
const SWITCH = {
  no_show: {
    icon: 'eyeoff',
    tone: 'g-amber',
    title: 'Switch to no-show?',
    yes: 'Yes, no-show',
    busy: 'Switching…',
    done: 'No-show — sales has it to chase',
    was: 'Completed',
    why: 'because the meeting time has passed',
    means: 'Saying no-show means nobody turned up.',
    points: [
      ['The badge becomes ', 'No-show', '.'],
      ['It goes to sales as a follow-up straight away, the same as pressing No-show on the day.', '', ''],
      ['Nothing is deleted, and pressing the badge again puts it back to completed.', '', '']
    ]
  },
  attended: {
    icon: 'check',
    tone: 'g-green',
    title: 'Switch back to completed?',
    yes: 'Yes, it happened',
    busy: 'Switching…',
    done: 'Back to completed',
    was: 'No-show',
    why: 'because somebody marked it so',
    means: 'Saying completed means the demo did happen after all.',
    points: [
      ['The badge goes back to ', 'Completed', '.'],
      ['The follow-up stays with sales — a demo that ran needs chasing too — but it stops asking them to chase a no-show.', '', ''],
      ['Nothing is deleted.', '', '']
    ]
  }
};

function SwitchAttendance({ b, to, reload, toast, onClose }) {
  const [busy, setBusy] = useState(false);
  const M = SWITCH[to] || SWITCH.no_show;

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.calendly.update(b.session.id, { attendance: to });
      toast(M.done);
      onClose();
      await reload();
    } catch (e) {
      toast(e.message);
      setBusy(false);
    }
  };

  /*
   * Out of the row, into the body.
   *
   * The dialog is position:fixed, and a transformed ancestor is what
   * fixed resolves against - .dbk-row lifts a pixel on hover, which is
   * exactly the state it is in when somebody has just clicked the badge
   * inside it. The whole dialog folded into the height of the row and
   * was clipped. Turning the transform off with :has() fixed the hover
   * but not the transition still settling, so the dialog leaves the row
   * altogether and nothing above it can catch it again.
   */
  return createPortal((
    <Sheet
      icon={M.icon}
      tone={M.tone}
      title={M.title}
      compact
      onClose={onClose}
      onSave={save}
      saveLabel={busy ? M.busy : M.yes}
      closeLabel="No"
    >
      <Group>What this does</Group>
      <div className="f wide dbk-ask">
        <p>
          <b>{b.invitee.name || b.invitee.email || 'This booking'}</b> is marked
          {' '}<b>{M.was}</b> {M.why}. {M.means}
        </p>
        <ul>
          {M.points.map(([a, strong, rest]) => (
            <li key={a}>{a}{strong ? <b>{strong}</b> : null}{rest}</li>
          ))}
        </ul>
      </div>
    </Sheet>
  ), document.body);
}

function RowActions({ b, reload, toast, onDone, onEdit }) {
  const s = b.session;
  const [busy, setBusy] = useState(false);
  if (!s) return null;

  const isB2B = s.customerType === 'B2B';

  const toB2B = async () => {
    setBusy(true);
    try {
      await api.calendly.markB2B(s.id);
      toast(`${b.invitee.name || 'This booking'} is a B2B partner`);
      /* Fold before the reload: the row is about to be redrawn with a
         partner badge, and the panel over it is describing the version
         that is going away. */
      onDone?.();
      await reload();
    } catch (e) { toast(e.message); } finally { setBusy(false); }
  };

  const remove = async () => {
    /* eslint-disable-next-line no-alert */
    if (!window.confirm(`Remove the demo with ${b.invitee.name || 'this person'}?

It goes from the list, the counts and every export. Calendly itself is not touched.`)) return;
    setBusy(true);
    try {
      await api.calendly.removeBooking(s.id);
      toast('Booking removed');
      /* The row itself is going; the panel has to go with it rather than
         sit open over whichever row takes its place. */
      onDone?.();
      await reload();
    } catch (e) { toast(e.message); } finally { setBusy(false); }
  };

  /*
   * Whether they turned up, for a meeting Calendly never saw.
   *
   * A Calendly booking gets this from the host pressing "no-show" over
   * there; a manual one has nobody to press it. Offered only once the
   * meeting has finished — before that there is nothing to say — and
   * reversible, because the first person to mark it may be wrong.
   */
  const over = new Date(b.end).getTime() < Date.now();
  const noShow = s.attendance === 'no_show';

  const setAttendance = async v => {
    setBusy(true);
    try {
      await api.calendly.update(s.id, { attendance: v });
      toast(v === 'no_show'
        ? 'Marked as a no-show — it goes to sales to chase'
        : 'Marked as attended');
      onDone?.();
      await reload();
    } catch (e) { toast(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="dbk-links dbk-danger">
      <span className="dim">
        {isB2B
          ? s.typeManual
            ? 'Marked as a B2B partner by hand.'
            : 'Booked as a B2B Meeting, so it counts as a partner.'
          : 'Booked as a customer meeting. If this is really a partner, say so here.'}
      </span>

      {/* Once it is with sales the two outcomes are off the row, so a
          wrong one is put right here. Nothing offers this until then -
          the row itself is the place while the row still asks. */}
      {s.isManual && over && followState(b) !== 'open' ? (
        <button type="button" className="btn sm" disabled={busy} onClick={() => setAttendance(noShow ? 'attended' : 'no_show')}>
          <Icon name={noShow ? 'check' : 'eyeoff'} />
          {noShow ? 'It was completed after all' : 'It was a no-show after all'}
        </button>
      ) : null}

      {/* Only a manual meeting can be corrected here. Calendly owns the
          details of its own bookings, and the row links out to it. */}
      {s.isManual ? (
        <button type="button" className="btn sm" disabled={busy} onClick={() => onEdit?.(b)}>
          <Icon name="edit" />Edit details
        </button>
      ) : null}

      {!isB2B ? (
        <button type="button" className="btn sm" disabled={busy} onClick={toB2B}>
          <Icon name="building" />Mark as B2B
        </button>
      ) : null}
      <button type="button" className="btn sm danger" disabled={busy} onClick={remove}>
        <Icon name="trash" />{s.isManual ? 'Delete' : 'Remove'}
      </button>
    </div>
  );
}

/**
 * The hand-over box: what happened, and it goes to sales.
 *
 * One field, because that is the whole ask - a no-show needs a line saying
 * so, a demo that happened needs a line saying how it went. Everything else
 * (the due date, the owner) is filled in behind it and can be changed in the
 * panel afterwards.
 */
export function StartFollowUp({ b, onCancel, onDone, toast }) {
  const s = b.session;
  const noShow = stateOf(b) === 'noshow';
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const t = text.trim();
    if (!t) { toast('Write what happened first'); return; }
    setBusy(true);
    try {
      /* The server answers with the session it wrote. Hand it straight back
         so the Follow-ups menu counts it now rather than after the bookings
         call, which is a minute of Calendly requests away. */
      const r = await api.calendly.followUp(s.id, { text: t });
      toast('Handed to sales — following up');
      onDone(r && r.session);
    } catch (e) {
      toast(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="dbk-start">
      <label>
        <span>{noShow ? 'They did not turn up — what next?' : 'How did the demo go?'}</span>
        <textarea
          autoFocus
          rows={2}
          value={text}
          placeholder={noShow
            ? 'e.g. No answer on the call, try again Tuesday'
            : 'e.g. Liked payroll, wants a quote for 12 staff'}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
        />
      </label>
      <div className="dbk-startacts">
        <button type="button" className="btn sm primary" disabled={busy} onClick={send}>
          {busy ? <Spinner /> : <Icon name="send" />}Send to follow-up
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={onCancel}>Cancel</button>
        <span className="dim">It appears on Sales &rarr; Follow-ups and the row is badged.</span>
      </div>
    </div>
  );
}

/*
 * The five ways a chase ends, in the order they are meant.
 *
 * The same keys the server takes and the journey panel writes — see
 * CLOSED_AS in routes/calendly.js and pendingClose(). "Closed" is last
 * because it is the one that says nothing, and it is only the right
 * answer when none of the other four is.
 */
const ENDINGS = ['registration', 'plan_upgrade', 'not_interested', 'demo_rescheduled', 'other'];

/**
 * Closing a follow-up: how it ended, and why.
 *
 * ## Two answers, not one
 *
 * This asked for a sentence and nothing else, and sent no category — so
 * the server filed every close under "other" and the Closed tab was a
 * column of identical grey badges reading "Closed". Whether the demo
 * ended in a registration or in "not interested" is the one thing that
 * tab exists to tell somebody, and it was being thrown away at the
 * moment it was known.
 *
 * So: a category, which the row wears afterwards, and the sentence,
 * which is still required — the category says what kind of ending it
 * was and the sentence says what actually happened, and neither
 * substitutes for the other.
 *
 * The journey panel closes the same follow-up by the same route and
 * reads its category off the last round written (pendingClose). This
 * one has no round to read, because it is the close somebody does
 * straight from the booking, so it asks.
 */
export function CloseFollowUp({ s, onCancel, onDone, toast }) {
  const [why, setWhy] = useState('');
  /* No default. "Registration" pre-selected would be a sale nobody made,
     and "Closed" pre-selected is the answer this box exists to stop
     everybody giving. */
  const [as, setAs] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const t = why.trim();
    if (!as) { toast('Say how it ended'); return; }
    if (!t) { toast('Say why it is being closed'); return; }
    setBusy(true);
    try {
      await api.calendly.update(s.id, { followStatus: 'closed', closedAs: as, closedReason: t });
      toast(`Closed — ${CLOSED_META[as][0]}`);
      onDone();
    } catch (e) {
      toast(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="dbk-start closing" onClick={e => e.stopPropagation()}>
      <div className="dbk-endings">
        <span>How did it end?</span>
        <div>
          {ENDINGS.map(k => {
            const [label, , icon] = CLOSED_META[k];
            return (
              <button
                key={k}
                type="button"
                className={`dbk-end ${k}${as === k ? ' on' : ''}`}
                onClick={() => setAs(k)}
                aria-pressed={as === k}
              >
                <Icon name={icon} />{label}
              </button>
            );
          })}
        </div>
      </div>
      <label>
        <span>Why is this being closed?</span>
        <textarea
          autoFocus
          rows={2}
          maxLength={200}
          value={why}
          placeholder="e.g. Bought the Growth plan / Not interested, budget / No answer after four calls"
          onChange={e => setWhy(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
        />
      </label>
      <div className="dbk-startacts">
        <button type="button" className="btn sm primary" disabled={busy} onClick={send}>
          {busy ? <Spinner /> : <Icon name="check" />}Close the follow-up
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={onCancel}>Cancel</button>
        <span className="dim">It can be reopened, and comes back where it left off.</span>
      </div>
    </div>
  );
}

/**
 * Sales picking up a hand-over: nothing to type, it is simply "mine now".
 */
export function StartWork({ s, onDone, toast, className }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className={className || 'btn sm primary'}
      disabled={busy}
      onClick={async e => {
        e.stopPropagation();
        setBusy(true);
        try {
          await api.calendly.update(s.id, { followStatus: 'following' });
          toast('Following up');
          onDone();
        } catch (err) {
          toast(err.message);
          setBusy(false);
        }
      }}
    >
      {busy ? <Spinner /> : <Icon name="flag" />}Start follow-up
    </button>
  );
}

/** Whether this booking belongs to a client of ours, in a word. */
function WhoPill({ b }) {
  if (!b.session && !b.client) return null;
  const [, tone, icon] = REG_META[REGISTERED(b)];
  /* A follow-up that ended in a registration says so here: the booking is
     where somebody asks "did this demo turn into anything", and the answer
     is a word on the row rather than a panel to open. */
  const s = b.session || {};
  if (s.closedAs === 'registration' || s.closedAs === 'plan_upgrade') {
    const won = s.closedAs === 'registration' ? 'Registered' : 'Upgraded';
    const plan = (b.client && b.client.plan) || '';
    return (
      <Pill tone="gr-green" icon="check" className="dbk-client">
        {plan ? `${won} · ${plan}` : won}
      </Pill>
    );
  }
  return (
    <Pill tone={tone} icon={icon || undefined} className="dbk-client">
      {regLabel(b)}
    </Pill>
  );
}

/* ----------------------------------------------------------- the work */

/**
 * The part of the row that is ours: who this is, what came of the demo, the
 * feedback, and who follows up by when. Every control saves on change;
 * there is no form to submit.
 */
export function DemoWork({ b, data, state, mayEdit, reload, refreshCrm, setSheet, toast, compact }) {
  const s = b.session;
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkQ, setLinkQ] = useState('');
  const [closing, setClosing] = useState(false);
  const { rows, crm } = useApp();
  const partner = isPartner(b);

  const save = async (patch, what) => {
    if (!mayEdit) return;
    setBusy(what || 'save');
    try {
      await api.calendly.update(s.id, patch);
      await reload();
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  const act = async (fn, done, what) => {
    if (!mayEdit) return;
    setBusy(what);
    try {
      const r = await fn();
      await Promise.all([reload(), refreshCrm ? refreshCrm() : null]);
      if (done) done(r);
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  const addFeedback = async () => {
    const text = note.trim();
    if (!text) return;
    setBusy('feedback');
    try {
      await api.calendly.feedback(s.id, text);
      setNote('');
      await reload();
      toast('Feedback recorded');
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  const past = state === 'done' || state === 'noshow' || state === 'live';
  const fs = followState(b);
  /* "Live" means the follow-up is still somebody's job - waiting to be
     picked up, or being chased. Closed is the only end. */
  const live = fs !== 'closed';

  /* Candidates for a hand-made link: the local client list and the sales
     contacts, by name, email or phone. */
  const candidates = useMemo(() => {
    const needle = linkQ.trim().toLowerCase();
    if (!needle || needle.length < 2) return [];
    const hit = v => String(v || '').toLowerCase().includes(needle);
    const clients = (rows || []).filter(r => hit(r.name) || hit(r.email) || hit(r.phone)).slice(0, 5)
      .map(r => ({ kind: 'client', id: r.id, name: r.name, sub: r.email || r.phone || '' }));
    const contacts = (crm.contacts || []).filter(c => hit(c.name) || hit(c.email) || hit(c.phone)).slice(0, 5)
      .map(c => ({ kind: 'contact', id: c.id, name: c.name, sub: c.email || c.phone || '' }));
    return [...clients, ...contacts];
  }, [linkQ, rows, crm.contacts]);

  return (
    <div className={`dbk-work${mayEdit ? '' : ' ro'}${compact ? ' one' : ''}`}>
      {/* Two readings of the same record, one panel each. The booking
          screen asks who this is - that is where a demo is matched to the
          Client Master. The follow-up list asks what is being done about
          it, and nothing else: whoever is chasing already knows who they
          are calling, the row says so above. */}
      {compact ? (
      <section>
        <h5>Who this is</h5>
        <div className="dbk-whobox">
          {b.client ? (
            <div className="dbk-match ok">
              <Icon name="check" />
              <div>
                <b>Registered client — {b.client.name}</b>
                <small>
                  {b.client.code ? <span className="dbk-code">{b.client.code}</span> : null}
                  {b.client.plan ? `${b.client.plan} plan · ` : ''}matched by {s.matchHow || 'hand'}
                </small>
              </div>
              {/^[0-9a-f-]{36}$/i.test(String(b.client.id)) ? (
                <button type="button" className="btn sm" onClick={() => setSheet({ kind: 'clientView', id: b.client.id })}>Open</button>
              ) : null}
            </div>
          ) : s.manualRegistered ? (
            <div className="dbk-match ok">
              <Icon name="check" />
              <div>
                <b>Registered — marked by hand</b>
                <small>
                  {b.contact && b.contact.code ? <span className="dbk-code">{b.contact.code}</span> : null}
                  They booked as {b.invitee.email || 'no email'}, which matches no account
                </small>
              </div>
            </div>
          ) : (
            <div className="dbk-match none">
              <Icon name="search" />
              <div>
                <b>Not registered</b>
                <small>No account has this email, phone or business. Mark them by hand from the row menu if they are a client.</small>
              </div>

            </div>
          )}

          {mayEdit ? (
            <div className="dbk-linkrow">
              {linking ? (
                <div className="dbk-linker">
                  <input
                    autoFocus
                    value={linkQ}
                    placeholder="Search clients and contacts by name, email or phone…"
                    onChange={e => setLinkQ(e.target.value)}
                  />
                  {candidates.length ? (
                    <ul>
                      {candidates.map(c => (
                        <li key={`${c.kind}:${c.id}`}>
                          <button type="button" onClick={() => act(
                            () => api.calendly.link(s.id, c.kind === 'client' ? { clientId: c.id } : { contactId: c.id }),
                            () => { setLinking(false); setLinkQ(''); toast(`Linked to ${c.name}`); }, 'link')}>
                            <Pill tone={c.kind === 'client' ? 'gr-teal' : 'gr-purple'} className="dbk-client">{c.kind}</Pill>
                            <b>{c.name}</b><small>{c.sub}</small>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : linkQ.trim().length >= 2 ? <small className="dim">Nobody matches.</small> : null}
                  <button type="button" className="btn sm" onClick={() => { setLinking(false); setLinkQ(''); }}>Cancel</button>
                </div>
              ) : (
                <>
                  <button type="button" className="lnk" onClick={() => setLinking(true)}>
                    {b.client || b.contact ? 'Link to someone else' : 'Link to an existing client or contact'}
                  </button>
                  {b.client || b.contact ? (
                    <button type="button" className="lnk" onClick={() => act(() => api.calendly.link(s.id, {}), () => toast('Unlinked'), 'link')}>Unlink</button>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
        </div>

        {s.interest ? <p className="dbk-hint dbk-interest"><b>Asked about:</b> {s.interest}</p> : null}
        {/* What whoever arranged the meeting wrote down about it. Kept
            beside the invitee's own answer, because the two are read
            together: what they asked for, and what we know. */}
        {s.demoNotes ? <p className="dbk-hint dbk-note"><b>Notes:</b> {s.demoNotes}</p> : null}
      </section>
      ) : (
      <section>
        <h5>
          {partner ? 'Feedback' : 'Follow-up'}
          <Pill
            tone={s.followStatus === 'converted' ? 'gr-green' : (FOLLOW_META[fs] || [])[1] || 'gr-slate'}
            icon={(FOLLOW_META[fs] || [])[2]}
            className="dbk-client"
          >
            {s.followStatus === 'converted' ? 'Converted' : (FOLLOW_META[fs] || [])[0] || 'Not handed over'}
          </Pill>
        </h5>

        {/* What the demo team sent across, kept at the top of the panel: it
            is the brief for the call sales is about to make. */}
        {s.handedBy ? (
          <div className="dbk-brief">
            <b><Icon name="send" />From the demo team &middot; {s.handedBy}</b>
            <p>{s.handedNote || 'No note was written.'}</p>
            {/* When it was handed over, not when the demo was held: this is
                the team's own clock, as the journey below it is. */}
            <small>{s.handedAt ? `${workStamp(s.handedAt)} · ${ago(s.handedAt)}` : ''}</small>
          </div>
        ) : null}

        {/* Feedback is the record on every kind of booking: what the partner
            said after a training session, what the customer said on the
            follow-up call. */}
        <Feedback list={s.feedback || []} />
        {mayEdit && past ? (
          <div className="dbk-addfb">
            <input
              value={note}
              placeholder={partner ? 'How did the session go?' : 'What did they say? e.g. reconnect next month'}
              onChange={e => setNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFeedback(); } }}
            />
            <button type="button" className="btn sm" disabled={!note.trim() || !!busy} onClick={addFeedback}>
              {busy === 'feedback' ? <Spinner /> : <Icon name="plus" />}Add
            </button>
          </div>
        ) : null}

        {live ? null : (
          <p className="dbk-hint">
            {s.followStatus === 'closed'
              ? `Closed${s.closedAsLabel ? ` with ${s.closedAsLabel}` : ''}${s.closedReason ? `: ${s.closedReason}` : ''}.`
              : `Marked ${s.followStatus}.`}
            {s.followPrev === 'handed' ? ' Reopening puts it back in the sales queue.' : ''}
          </p>
        )}

        {closing ? (
          <CloseFollowUp
            s={s}
            toast={toast}
            onCancel={() => setClosing(false)}
            onDone={() => { setClosing(false); reload(); }}
          />
        ) : null}

        {/* Three things can be done to a follow-up and no more: take it on,
            finish it with a reason, or put it back. */}
        <div className="dbk-actions">
          {mayEdit && fs === 'handed' ? (
            <StartWork s={s} toast={toast} onDone={reload} />
          ) : null}
          {mayEdit ? (live ? (
            <button type="button" className="btn sm" disabled={!!busy || closing} onClick={() => setClosing(true)}>
              <Icon name="x" />Close follow-up
            </button>
          ) : (
            /* Reopening returns it to where it was closed from, so a
               follow-up sales was chasing does not land back in the queue
               as though nobody had touched it. */
            <button type="button" className="btn sm" disabled={!!busy}
              onClick={() => save({ followStatus: s.followPrev || 'following' }, 'status')}>
              <Icon name="undo" />Reopen
            </button>
          )) : null}
          {busy ? <Spinner /> : null}
        </div>
      </section>
      )}
    </div>
  );
}

/**
 * What this person's other demos came to - the ones before this one, and
 * the ones booked since.
 *
 * A partner's fourth session is read with the first three beside it, and a
 * customer who has been round twice should not be followed up as though
 * this were the first conversation. The later ones matter as much: a no-show
 * who booked again is not somebody to chase.
 */
function EarlierDemos({ b, data }) {
  const earlier = earlierDemos(b, data);
  const later = laterDemos(b, data);
  if (!earlier.length && !later.length) return null;
  const who = b.client ? 'client' : 'person';

  return (
    <>
      {later.length ? (
        <section className="dbk-prev">
          <header>
            <Icon name="cal" />
            <b>After this one</b>
            <span>{later.length} later demo{later.length === 1 ? '' : 's'} with this {who}</span>
          </header>
          <ol>{later.map(x => <OtherDemo key={x.id} x={x} b={b} />)}</ol>
        </section>
      ) : null}
      {earlier.length ? (
        <section className="dbk-prev">
          <header>
            <Icon name="clock" />
            <b>Before this one</b>
            <span>{earlier.length} earlier demo{earlier.length === 1 ? '' : 's'} with this {who}</span>
          </header>
          <ol>{earlier.map(x => <OtherDemo key={x.id} x={x} b={b} />)}</ol>
        </section>
      ) : null}
    </>
  );
}

function OtherDemo({ x, b }) {
  const st = stateOf(x);
  const [label, tone, icon] = STATE_META[st];
  const xs = x.session || {};
  const closed = xs.followStatus === 'closed';
  /* Matched on the phone, booked under another name: say which, or nobody
     can find it on Calendly. */
  const other = String(x.invitee.email || '').toLowerCase() !== String(b.invitee.email || '').toLowerCase();
  return (
    <li className={`dbk-prevcard ${st}`}>
      <div className="dbk-prevhead">
        <span className="dbk-prevno">{xs.demoIndex || '·'}</span>
        <div>
          <b>{stamp(x.start)}</b>
          <small>{x.eventTypeName || x.name} · {x.duration} min{x.host.name ? ` · ${x.host.name}` : ''}</small>
          {other ? (
            <small className="dbk-prevas">
              Booked as {x.invitee.name || x.invitee.email}{x.invitee.name && x.invitee.email ? ` · ${x.invitee.email}` : ''}
            </small>
          ) : null}
        </div>
        <Pill tone={tone} icon={icon}>{label}</Pill>
      </div>

      <div className="dbk-prevbody">
        <div className="dbk-prevrow">
          <dt>Outcome</dt>
          <dd>{xs.outcomeLabel || (st === 'noshow' ? 'Did not turn up' : st === 'canceled' ? 'Cancelled' : 'Not recorded')}</dd>
        </div>
        {xs.demoNotes ? (
          <div className="dbk-prevrow wide"><dt>Notes</dt><dd>{xs.demoNotes}</dd></div>
        ) : null}
        <div className="dbk-prevrow">
          <dt>Follow-up</dt>
          <dd>
            {closed ? `Closed — ${xs.closedReason || 'no reason given'}`
              : xs.followStatus === 'following' ? `Following up${xs.ownerName ? ` · ${xs.ownerName}` : ''}`
                : xs.followStatus === 'handed' ? 'Waiting on sales'
                  : 'Never started'}
          </dd>
        </div>
      </div>

      {(xs.feedback || []).length ? (
        <ul className="dbk-prevfb">
          {xs.feedback.map((f, k) => (
            <li key={k}>
              <p>{f.text}</p>
              <small>{f.by || 'system'} · {stamp(f.at)}</small>
            </li>
          ))}
        </ul>
      ) : (
        <p className="dbk-prevnone">No feedback was written on that one.</p>
      )}
    </li>
  );
}

/** Every round of feedback, oldest first. */
export function Feedback({ list }) {
  if (!list.length) return null;
  return (
    <ul className="dbk-fb">
      {list.map((f, i) => (
        <li key={i}>
          <p>{f.text}</p>
          <small>{f.by || 'system'} · {stamp(f.at)}</small>
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------- helpers */

const shortDate = iso => dayText(`${iso}T12:00:00+04:00`).replace(/ \d{4}$/, '');
/** "16 Sep" from a full timestamp, Dubai's day. */
export const dayShort = iso => dayText(iso).replace(/ \d{4}$/, '');
export const plusDays = n => {
  const d = new Date(Date.now() + n * DAY);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai' }).format(d);
};
